import {
	BagPaymentOrganization,
	BagPaymentTermListTableColumn,
	BagPaymentTermTableItem,
} from '@/bag/bag-payment-term-list/bag-payment-term-list.model';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	OnInit,
	output,
	viewChild,
	input,
	signal,
} from '@angular/core';
import { MatPaginator, MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { Modal } from 'bootstrap';

interface BagPaymentTermTableSettings {
	hiddenColumns: string[];
}

@Component({
	selector: 'app-bag-payment-term-list-table',
	templateUrl: './bag-payment-term-list-table.component.html',
	styleUrls: ['./bag-payment-term-list-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BagPaymentTermListTableComponent implements OnInit, AfterViewInit {
	readonly elements = input<BagPaymentTermTableItem[]>([]);
	readonly tableColumns = input<BagPaymentTermListTableColumn[]>([]);
	readonly agencies = input<BagPaymentOrganization[]>([]);
	readonly allItemsSize = input(0);
	readonly pageIndex = input(0);
	readonly pageSize = input(50);
	readonly page = output<PageEvent>();
	readonly exportCsv = output<void>();
	readonly sortChanged = output<string | null>();
	readonly addRefund = output<BagPaymentTermTableItem>();

	private readonly columnToOrderingField: Record<string, string> = {
		term: 'term',
		value: 'value',
		status: 'status',
		reservationSeller: 'reservation_seller',
		ownerName: 'owner_name',
		invoiceNumber: 'invoice_number',
		tableId: 'table_id',
	};

	readonly paginator = viewChild(MatPaginator);

	readonly settings = signal<BagPaymentTermTableSettings>({ hiddenColumns: [] });
	readonly viewIsSet = computed(() => this.settings().hiddenColumns.length > 0);
	readonly tableKeys = computed(() => [...this.tableColumns().map((column) => column.id), 'columnShow']);
	readonly showColumns = computed(() =>
		this.tableKeys().filter((columnId) => !this.settings().hiddenColumns.includes(columnId)),
	);
	readonly hasNoResults = computed(() => !this.elements()?.length);
	readonly tableData = computed(() => this.elements());
	readonly agencyShortNameById = computed<Record<string | number, string>>(() => {
		return this.agencies().reduce<Record<string | number, string>>((acc, agency) => {
			acc[agency.id] = agency.shortName;
			return acc;
		}, {});
	});

	protected readonly module = 'paymentTermList';

	private readonly paginatorIntl = inject(MatPaginatorIntl);

	ngOnInit(): void {
		this.setPaginatorTranslations();
		this.loadSettings();
	}

	ngAfterViewInit(): void {
		const paginator = this.paginator();
		if (!paginator) {
			return;
		}
	}

	showHideRow(itemId: string): void {
		this.settings.update((current) => {
			const hiddenColumns = [...current.hiddenColumns];
			const index = hiddenColumns.indexOf(itemId);
			if (index === -1) {
				hiddenColumns.push(itemId);
			} else {
				hiddenColumns.splice(index, 1);
			}
			return { hiddenColumns };
		});
		this.saveSettings();
	}

	onExportCsv(): void {
		this.exportCsv.emit();
	}

	onAddRefund(element: BagPaymentTermTableItem): void {
		this.addRefund.emit(element);
	}

	dialogClose(): void {
		const modal = Modal.getInstance(document.getElementById('addModal'));
		if (!modal) {
			return;
		}
		modal.hide();
	}

	openDialog(): void {
		new Modal(document.getElementById('addModal')).show();
	}

	pageChanged(event: PageEvent): void {
		this.page.emit(event);
	}

	isSortable(columnId: string): boolean {
		return columnId in this.columnToOrderingField;
	}

	onSortChange(sort: Sort): void {
		const backendField = this.columnToOrderingField[sort.active];
		if (!backendField || !sort.direction) {
			this.sortChanged.emit(null);
			return;
		}
		const ordering = sort.direction === 'desc' ? `-${backendField}` : backendField;
		this.sortChanged.emit(ordering);
	}

	private setPaginatorTranslations(): void {
		this.paginatorIntl.itemsPerPageLabel = 'Pozycji na stronie:';
		this.paginatorIntl.firstPageLabel = 'Pierwsza strona';
		this.paginatorIntl.lastPageLabel = 'Ostatnia strona';
		this.paginatorIntl.nextPageLabel = 'Następna strona';
		this.paginatorIntl.previousPageLabel = 'Poprzednia strona';
		this.paginatorIntl.changes.next();
	}

	private saveSettings(): void {
		localStorage.setItem(`pageSettings-${this.module}`, JSON.stringify(this.settings()));
	}

	private loadSettings(): void {
		const storedSettings = localStorage[`pageSettings-${this.module}`];
		if (!storedSettings) {
			return;
		}
		this.settings.set(JSON.parse(storedSettings));
	}
}
